import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, X, Edit2, Check, Info, Plus } from 'lucide-react';

const AssumptionsPanel = ({ 
  isOpen, 
  onToggle, 
  assumptions, 
  onAssumptionChange,
  months,
  mode = 'budget', // 'budget' or 'forecast'
  forecastDriverExplanations = {},
  panelTitle = 'Budget Assumptions' // Default title
}) => {
  const [editingAssumption, setEditingAssumption] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // Top-level metrics that can be set monthly
  const topLevelMetrics = [
    { id: 'adr', name: 'ADR', description: 'Average Daily Rate', unit: '$', defaultValue: 150 },
    { id: 'guestsPerRoom', name: 'Guests per Room', description: 'Average number of guests per occupied room', unit: '', defaultValue: 1.3 },
    { id: 'occupancyRate', name: 'Occupancy %', description: 'Percentage of rooms occupied', unit: '%', defaultValue: 75 },
    { id: 'availableRooms', name: 'Available Rooms', description: 'Number of rooms available', unit: '', defaultValue: 100 }
  ];
  
  // Initialize assumptions if not provided
  useEffect(() => {
    if (!assumptions || Object.keys(assumptions).length === 0) {
      const initialAssumptions = {};
      
      // Initialize top level metrics for each month
      if (months && months.length > 0) {
        months.forEach(month => {
          initialAssumptions[month] = topLevelMetrics.reduce((acc, metric) => {
            acc[metric.id] = metric.defaultValue;
            return acc;
          }, {});
        });
      }
      
      // Initialize default line-level assumptions
      initialAssumptions.lineAssumptions = {};
      
      onAssumptionChange(initialAssumptions);
    }
  }, [months, onAssumptionChange, assumptions]);
  
  const handleAssumptionEdit = (metricId, month) => {
    setEditingAssumption({ metricId, month });
    setEditValue(assumptions[month][metricId]);
  };
  
  const saveAssumptionEdit = () => {
    if (!editingAssumption) return;
    
    const { metricId, month } = editingAssumption;
    const newValue = parseFloat(editValue);
    
    if (isNaN(newValue)) {
      // Invalid value - revert
      setEditingAssumption(null);
      setEditValue('');
      return;
    }
    
    // Update assumption
    const updatedAssumptions = { ...assumptions };
    updatedAssumptions[month] = {
      ...updatedAssumptions[month],
      [metricId]: newValue
    };
    
    onAssumptionChange(updatedAssumptions);
    setEditingAssumption(null);
    setEditValue('');
  };
  
  const cancelAssumptionEdit = () => {
    setEditingAssumption(null);
    setEditValue('');
  };
  
  // Format numbers with appropriate units
  const formatValue = (value, unit) => {
    if (unit === '%') {
      return `${value}%`;
    } else if (unit === '$') {
      return `$${value}`;
    }
    return value;
  };
  
  // Add a new line-level assumption
  const [showNewAssumptionForm, setShowNewAssumptionForm] = useState(false);
  const [newAssumption, setNewAssumption] = useState({
    accountId: '',
    name: '',
    type: 'perGuest', // perGuest, perRoom, perDay, fixed
    value: '',
    unit: '$'
  });
  
  const assumptionTypes = [
    { id: 'perGuest', label: 'Per Guest' },
    { id: 'perRoom', label: 'Per Room' },
    { id: 'perDay', label: 'Per Day' },
    { id: 'fixed', label: 'Fixed Amount' },
    { id: 'directAmount', label: 'Direct Amount' }  // New option for direct amounts
  ];
  
  const addLineAssumption = () => {
    if (!newAssumption.accountId || !newAssumption.name || !newAssumption.value) {
      return; // Require fields
    }
    
    const value = parseFloat(newAssumption.value);
    if (isNaN(value)) return;
    
    // Update line assumptions
    const updatedAssumptions = { ...assumptions };
    if (!updatedAssumptions.lineAssumptions) {
      updatedAssumptions.lineAssumptions = {};
    }
    
    updatedAssumptions.lineAssumptions[newAssumption.accountId] = {
      name: newAssumption.name,
      type: newAssumption.type,
      value: value,
      unit: newAssumption.unit,
      // For direct amounts, store monthly values
      monthlyValues: newAssumption.type === 'directAmount' ? {} : undefined
    };
    
    onAssumptionChange(updatedAssumptions);
    setShowNewAssumptionForm(false);
    setNewAssumption({
      accountId: '',
      name: '',
      type: 'perGuest',
      value: '',
      unit: '$'
    });
  };
  
  const removeLineAssumption = (accountId) => {
    const updatedAssumptions = { ...assumptions };
    if (updatedAssumptions.lineAssumptions && updatedAssumptions.lineAssumptions[accountId]) {
      delete updatedAssumptions.lineAssumptions[accountId];
      onAssumptionChange(updatedAssumptions);
    }
  };
  
  // Custom tooltip component for explanations
  const Tooltip = ({ children, content }) => {
    const [isVisible, setIsVisible] = useState(false);
    
    return (
      <div className="relative inline-block">
        <div 
          onMouseEnter={() => setIsVisible(true)}
          onMouseLeave={() => setIsVisible(false)}
          className="cursor-help"
        >
          {children}
        </div>
        {isVisible && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-60 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg z-20">
            {content}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
          </div>
        )}
      </div>
    );
  };

  const handleInputChange = (month, value) => {
    if (mode === 'forecast') return; // No input changes in forecast mode

    const newAssumptions = {
      ...assumptions,
      [month]: parseFloat(value) || 0, // Ensure it's a number
    };
    onAssumptionChange(newAssumptions);
  };

  // Define driver types based on the keys from forecastDriverExplanations
  const driverTypes = Object.keys(forecastDriverExplanations);

  return (
    <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
      <button
        className="w-full flex justify-between items-center p-4 focus:outline-none"
        onClick={onToggle}
      >
        <div className="flex items-center">
          <Info size={18} className="text-orange-500 mr-2" />
          <h3 className="text-md font-semibold text-gray-700">{panelTitle}</h3>
        </div>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>

      {isOpen && (
        <div className="p-4 border-t border-gray-200">
          {mode === 'budget' && (
            <>
              <p className="text-sm text-gray-600 mb-3">
                Enter your monthly assumptions below. These can be used in budget calculations within the table using formulas like <code className="text-xs bg-orange-100 text-orange-700 p-0.5 rounded">=[Guests]*0.5</code> or <code className="text-xs bg-orange-100 text-orange-700 p-0.5 rounded">=[Fixed Cost]</code>.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {months.map((monthLabel) => (
                  <div key={monthLabel}>
                    <label 
                      htmlFor={`assumption-${monthLabel}`} 
                      className="block text-xs font-medium text-gray-700 mb-1"
                    >
                      {monthLabel}
                    </label>
                    <input
                      type="number"
                      id={`assumption-${monthLabel}`}
                      name={`assumption-${monthLabel}`}
                      value={assumptions[monthLabel] || ''} // Use monthLabel as key
                      onChange={(e) => handleInputChange(monthLabel, e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm shadow-sm focus:ring-orange-500 focus:border-orange-500"
                      placeholder="e.g. 100"
                    />
                  </div>
                ))}
              </div>
            </>
          )}
          {mode === 'forecast' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-3">
                Forecasts can be driven by different logic types. Select a driver for each account row in the table below. Here's how each driver works:
              </p>
              {driverTypes.map(driverName => (
                <div key={driverName} className="p-3 bg-white rounded-md border border-gray-200 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">{driverName}</h4>
                  <p className="text-xs text-gray-600">
                    {forecastDriverExplanations[driverName]}
                  </p>
                </div>
              ))}
              {driverTypes.length === 0 && (
                <p className="text-sm text-gray-500">No driver explanations available.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssumptionsPanel; 